import * as React from "react";
import { cn } from "~/lib/utils";
import { Eye, EyeOff, AlertCircle, CheckCircle2 } from "lucide-react";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string;
  success?: boolean;
  showPasswordToggle?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, error, success, showPasswordToggle, "aria-describedby": ariaDescribedBy, ...props }, ref) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const isPassword = type === "password";
    const inputType = isPassword && showPassword ? "text" : type;

    return (
      <div className="relative w-full">
        <input
          type={inputType}
          aria-invalid={!!error}
          aria-describedby={ariaDescribedBy}
          autoComplete="off"
          data-form-type="other"
          data-lpignore="true"
          data-1p-ignore="true"
          className={cn(
            "flex h-11 w-full rounded-lg border px-4 py-2 text-sm transition-all duration-200",
            "bg-background/60 backdrop-blur-sm",
            "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground",
            "placeholder:text-muted-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:bg-background/80",
            "hover:bg-background/70",
            "disabled:cursor-not-allowed disabled:opacity-50",
            error
              ? "border-destructive/50 focus-visible:ring-destructive/50 focus-visible:border-destructive"
              : success
                ? "border-success/50 focus-visible:ring-success/50 focus-visible:border-success"
                : "border-input/50 focus-visible:ring-ring/30 focus-visible:border-ring",
            isPassword && showPasswordToggle && "pr-10",
            className
          )}
          ref={ref}
          {...props}
        />
        {isPassword && showPasswordToggle && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            tabIndex={-1}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
        {error && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-destructive">
            {!isPassword && <AlertCircle className="h-4 w-4" />}
          </div>
        )}
        {success && !error && !isPassword && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-success">
            <CheckCircle2 className="h-4 w-4" />
          </div>
        )}
      </div>
    );
  }
);
Input.displayName = "Input";

export { Input };
